import type { LoginResponse, LoginUser, UserDTO } from '@/shared/api/types/auth-types'

interface JwtClaims {
  sub?: string
  user_id?: string
  id?: string
  email?: string
  username?: string
  name?: string
  full_name?: string
  avatarUrl?: string
  role?: string
  roles?: string[] | string
  tenant_id?: string
  tenant?: {
    id?: string
    slug?: string
    name?: string
  }
}

function toNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

export function parseJwtClaims(token: string): JwtClaims {
  try {
    const [, payloadBase64] = token.split('.')
    if (!payloadBase64) return {}

    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(
      normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '='),
    )
    return JSON.parse(json) as JwtClaims
  } catch {
    return {}
  }
}

export function extractRoles(claims: JwtClaims): string[] {
  if (Array.isArray(claims.roles)) {
    const roles = claims.roles.filter(
      (item): item is string => typeof item === 'string' && item.length > 0,
    )
    if (roles.length > 0) {
      return roles
    }
  }

  if (typeof claims.roles === 'string' && claims.roles.length > 0) {
    return [claims.roles]
  }

  if (typeof claims.role === 'string' && claims.role.length > 0) {
    return [claims.role]
  }

  return []
}

function normalizeRole(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const normalized = value.trim()
  if (!normalized) {
    return undefined
  }
  return normalized
}

export function extractRole(claims: JwtClaims): string {
  const [firstRole] = extractRoles(claims)
  return firstRole ?? 'unknown'
}

export function mapTokenToUser(
  accessToken: string,
  usernameOrEmail: string,
  options?: { role?: string | null | undefined },
): LoginResponse['user'] {
  const claims = parseJwtClaims(accessToken)
  const roleFromPayload = normalizeRole(options?.role)
  const rolesFromClaims = extractRoles(claims)
  const roles = roleFromPayload ? [roleFromPayload] : rolesFromClaims
  const username = toNonEmptyString(claims.username) ?? usernameOrEmail
  const email = toNonEmptyString(claims.email) ?? usernameOrEmail
  const fullName = toNonEmptyString(claims.full_name) ?? toNonEmptyString(claims.name)
  const tenantId = toNonEmptyString(claims.tenant_id) ?? toNonEmptyString(claims.tenant?.id)
  const tenantSlug = toNonEmptyString(claims.tenant?.slug)
  const user: LoginUser = {
    id: claims.user_id ?? claims.id ?? claims.sub ?? usernameOrEmail,
    name: fullName ?? username,
    username,
    email,
    role: roles[0] ?? roleFromPayload ?? 'unknown',
    roles,
    tenantId: tenantId ?? null,
    ...(tenantSlug ? { tenantSlug } : {}),
  }

  if (fullName) {
    user.fullName = fullName
  }
  const avatarUrl = toNonEmptyString(claims.avatarUrl)
  if (avatarUrl) {
    user.avatarUrl = avatarUrl
  }
  if (claims.tenant?.id) {
    user.tenant = {
      id: claims.tenant.id,
      ...(claims.tenant.slug ? { slug: claims.tenant.slug } : {}),
      ...(claims.tenant.name ? { name: claims.tenant.name } : {}),
    }
  }

  return user
}

/**
 * Gộp thông tin user lấy từ profile API (`UserDTO`) với thông tin trong JWT access token.
 *
 * Thứ tự ưu tiên dữ liệu:
 * - Luôn lấy id/tenantId/tenant từ profile khi có (API là nguồn sự thật).
 * - Fallback sang token khi profile thiếu slug/name tenant hoặc email.
 * - Role/roles chủ yếu đọc từ token (vì có thể chứa nhiều role hơn).
 *
 * @param userDto - User lấy từ API `/me` hoặc tương đương.
 * @param accessToken - Access token JWT hiện tại (có thể `null`/`undefined` nếu chưa có hoặc đã hết hạn).
 * @returns Đối tượng `LoginResponse['user']` đã được chuẩn hóa, dùng thống nhất trong app.
 */
export function mergeUserProfileWithToken(
  userDto: UserDTO,
  accessToken?: string | null,
): LoginResponse['user'] {
  const tokenUser = accessToken ? mapTokenToUser(accessToken, userDto.username) : null
  const dtoTenant = userDto.tenant ?? null
  const tokenTenant = tokenUser?.tenant ?? null
  const normalizedFullName = userDto.fullName?.trim()

  // Ưu tiên dữ liệu profile từ API; chỉ fallback về token khi profile thiếu.
  const name =
    normalizedFullName && normalizedFullName.length > 0 ? normalizedFullName : userDto.username
  const tenantId = userDto.tenantId ?? tokenTenant?.id
  const tenantSlug =
    toNonEmptyString(dtoTenant?.slug) ??
    toNonEmptyString(tokenTenant?.slug) ??
    toNonEmptyString(tokenUser?.tenantSlug)
  const tenantName = toNonEmptyString(dtoTenant?.name) ?? toNonEmptyString(tokenTenant?.name)

  // Chỉ dựng tenant khi có tenantId để đảm bảo đúng kiểu LoginUserTenant (id là bắt buộc).
  const tenant = tenantId
    ? {
        id: tenantId,
        ...(tenantSlug ? { slug: tenantSlug } : {}),
        ...(tenantName ? { name: tenantName } : {}),
      }
    : undefined

  const mergedUser: LoginResponse['user'] = {
    id: userDto.id,
    name,
    username: userDto.username,
    email: userDto.email ?? tokenUser?.email ?? userDto.username,
    role: tokenUser?.role ?? 'unknown',
    roles: tokenUser?.roles ?? [],
    tenantId: userDto.tenantId,
    ...(tenantSlug ? { tenantSlug } : {}),
    ...(tenant ? { tenant } : {}),
  }

  if (userDto.fullName) {
    mergedUser.fullName = userDto.fullName
  }

  if (userDto.avatarUrl) {
    mergedUser.avatarUrl = userDto.avatarUrl
  }

  return mergedUser
}
